import { useEffect, useState } from 'react';
import { Card, Switch, Input, Button, Tooltip, Typography, notification } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import ResellerDashboard from '../ResellerDashboard';
import { ResellerSiderItem } from 'src/enums/IResellerSiderItems';
import { Link } from 'react-router-dom';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getResellerData } from '@/store/reseller/selector';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateIntercomAppId } from '@/api/reseller';

const { Title, Text } = Typography;

const ResellerIntercomSupport = () => {
	const queryClient = useQueryClient();
	const currentReseller = useAppSelector(getResellerData);

	const [intercomEnabled, setIntercomEnabled] = useState(false);
	const [intercomAppId, setIntercomAppId] = useState('');

	const saveIntercomId = useMutation({
		mutationFn: updateIntercomAppId,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['reseller'] });
			notification.success({
				message: 'Intercom App ID saved successfully',
			});
		},
		onError: () => {
			notification.error({
				message: 'Failed to save Intercom App ID',
			});
		},
	});

	useEffect(() => {
		if (currentReseller?.emailAddress) {
			const intercomAppID = currentReseller.intercom_app_id;
			setIntercomEnabled(!!intercomAppID);

			setIntercomAppId(intercomAppID || '');
		}
	}, [currentReseller]);

	const handleToggle = (checked: boolean) => {
		setIntercomEnabled(checked);
	};

	const handleSubmit = () => {
		if (intercomEnabled) {
			saveIntercomId.mutate({ intercom_app_id: intercomAppId });
		} else {
			saveIntercomId.mutate({ intercom_app_id: '' });
		}
	};

	return (
		<ResellerDashboard activeKey={ResellerSiderItem.INTERCOM_SUPPORT}>
			<div className="p-4">
				<div className="flex items-center justify-between mb-6">
					<div>
						<Title level={2} className="!mb-2">
							Intercom Support
						</Title>
						<Text type="secondary">
							You can now easily connect your own Intercom account to offer direct support to your
							clients through their dashboards. Simply set up your Intercom account at{' '}
							<Link
								className="!underline !text-gray-400"
								to="https://www.intercom.com/"
								target="_blank"
							>
								https://www.intercom.com/
							</Link>{' '}
							and enter your APP ID—once added, it will automatically integrate into your clients'
							dashboards. With this integration, you can offer your clients chat support directly
							within their dashboards, ensuring prompt response time
						</Text>
					</div>
				</div>
				<Card className="w-full lg:max-w-[800px]">
					<div className="flex items-center justify-between mb-8">
						<div className="flex items-center gap-2">
							<span className="text-lg font-medium">Intercom Integration</span>
							<Tooltip title="Enable Intercom to provide in-app support for your customers.">
								<InfoCircleOutlined className="ml-2 text-gray-400" />
							</Tooltip>
						</div>
						<Switch checked={intercomEnabled} onChange={handleToggle} />
					</div>

					<div style={{ marginTop: 32 }}>
						<div className="font-medium mb-2">Intercom App ID</div>
						<Input
							placeholder="Enter your Intercom App ID"
							value={intercomAppId}
							onChange={(e) => setIntercomAppId(e.target.value)}
							disabled={!intercomEnabled}
						/>
						<div className="text-gray-400 mt-2 !text-sm">
							You can find your App ID under{' '}
							<Link
								className="underline"
								to="https://developers.intercom.com/installing-intercom/web/installation#basic-javascript"
								target="_blank"
							>
								Installation
							</Link>{' '}
							section.
						</div>
					</div>

					<div className="flex justify-end mt-8">
						<Button
							loading={saveIntercomId.isPending}
							type="primary"
							onClick={handleSubmit}
							disabled={(!intercomEnabled && !intercomAppId) || !intercomAppId}
						>
							Save Changes
						</Button>
					</div>
				</Card>
			</div>
		</ResellerDashboard>
	);
};

export default ResellerIntercomSupport;
